import { ProviderSelector } from "placement/components/provider-selection/ProviderSelector";
import { setProviderViewAsSession } from "providerapp/core/persistence/provider-view-as-state-magement";
import { InternalUserProviderViewAsContext } from "providerapp/InternalUserProviderViewAs";
import React, { useContext } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { useHistory } from "react-router";

type ProviderSelectionModalProps  = {
    visible: boolean;
    onClose: () => void;
    onSubmitCallback?: () => void;
    title?: string;
    maxWidth?: string;
    customModalTopPosition?: string | number;
}
type FormValues = {
    providerGuid: string;
};

export const ProviderSelectionModal = ({visible, onClose, onSubmitCallback, title = 'Change Provider', maxWidth = 'max-w-3xl', customModalTopPosition = 0}: ProviderSelectionModalProps) => {
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const {setProviderGuid} = useContext(InternalUserProviderViewAsContext)
    const history = useHistory();
    
    const methods = useForm<FormValues>({
        mode: 'onChange',
        defaultValues: {
            providerGuid: ''
        }
    });
    const onSubmit = async (data: FormValues) => {
        if (!data.providerGuid) return;
        
        setIsSubmitting(true);
        // revoke findProvider cache
        // Store the selected provider guid in session storage
        setProviderViewAsSession(data.providerGuid);
        setProviderGuid(data.providerGuid);
        // Reload the page
        if (onSubmitCallback) {
            onSubmitCallback();
        } else {
            history.go(0);
        }
    };

    return (
        <div
            className={(visible ? "" : "hidden ") + "overflow-y-auto overflow-x-hidden fixed z-50 flex justify-center items-center h-modal h-full inset-0"}
            style={{backgroundColor: "rgba(0,0,0,0.5)"}}>
            <div className={`relative px-4 w-full ${maxWidth} h-auto max-h-[90vh] flex items-center`}
                 style={{margin: "auto"}}>
                <div className="relative w-full bg-white rounded-lg shadow-lg dark:bg-gray-700 transition-all duration-300 ease-in-out"
                     style={customModalTopPosition === 0 ? {} : {top: customModalTopPosition}}>
                    <div className="flex justify-between items-center p-3 rounded-t border-b border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-800">
                        <h3 className="text-base font-semibold text-gray-900 dark:text-white flex items-center">
                            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                            </svg>
                            {title}
                        </h3>
                        <button type="button"
                                className="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-full text-sm p-1.5 ml-auto inline-flex items-center dark:hover:bg-gray-600 dark:hover:text-white transition-colors duration-200"
                                onClick={onClose}
                                aria-label="Close"
                        >
                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"
                                 xmlns="http://www.w3.org/2000/svg">
                                <path fillRule="evenodd"
                                      d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                                      clipRule="evenodd"/>
                            </svg>
                        </button>
                    </div>
                    <div className="w-full flex flex-col items-center justify-center p-5 space-y-4 bg-white rounded-b-lg">
                        <div className="text-center">
                            <h1 className="text-xl font-bold text-gray-900">Provider Selection</h1>
                            <p className="mt-1 text-sm text-gray-600">
                                Please select a provider to view
                            </p>
                        </div>
                        <FormProvider {...methods}>
                            <form onSubmit={methods.handleSubmit(onSubmit)} className="mt-4 space-y-5 w-full max-w-md">
                            <div className="w-full">
                                <ProviderSelector />
                            </div>

                        <div className="pt-2 pb-4">
                            <button
                                type="submit"
                                disabled={!methods.watch('providerGuid') || isSubmitting}
                                className={`w-full flex justify-center items-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white transition-all duration-200 ${!methods.watch('providerGuid') || isSubmitting ? 'bg-gray-400 cursor-not-allowed' : 'bg-atlantis-200 hover:bg-atlantis-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-atlantis-100'}`}
                            >
                                {isSubmitting ? (
                                    <>
                                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        Loading...
                                    </>
                                ) : (
                                    <>
                                        Continue
                                        <svg className="ml-2 -mr-1 w-4 h-4" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                                            <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd"></path>
                                        </svg>
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                        </FormProvider>
                    </div>
                </div>
            </div>
        </div>
    )

}